import React, { useState } from 'react';
import { Card, Tag, Typography, Space, Button, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useGetTNListQuery, useDeleteTNMutation, TNDto } from '../../store/api/tnApi';
import { useGetDriversQuery } from '../../store/api/referencesApi';
import { API_BASE_URL, STORAGE_KEYS } from '../../constants';
import { DataTable, PageLayout } from '../../ui';

const { Text } = Typography;

const statusColor: Record<string, string> = {
  draft: 'default',
  ready: 'blue',
  printed: 'gold',
  signed: 'green'
};

const TNPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const navigate = useNavigate();

  const { data, isLoading, refetch } = useGetTNListQuery({ page, limit });
  const [deleteTN] = useDeleteTNMutation();
  const { data: drivers = [] } = useGetDriversQuery();
  const driverIdToName = React.useMemo(() => {
    return new Map<number, string>((drivers || []).map((d: any) => [d.id, d.fullName]));
  }, [drivers]);

  const columns: ColumnsType<TNDto> = [
    {
      title: '№',
      dataIndex: 'number',
      key: 'number',
      width: 120,
      className: 'primary-cell',
      render: (n: string) => <Tag color="blue">{n}</Tag>
    },
    {
      title: 'Грузоотправитель',
      key: 'shipper',
      width: 220,
      render: (_, r) => r.shipperName || (r.shipper && r.shipper.name) || '—'
    },
    {
      title: 'Грузополучатель',
      key: 'consignee',
      width: 220,
      render: (_, r) => r.consigneeName || (r.consignee && r.consignee.name) || '—'
    },
    {
      title: 'Водитель',
      key: 'driver',
      width: 220,
      render: (_, r) => r.driverName || (r.driver && r.driver.fullName) || (r.driverId ? (driverIdToName.get(r.driverId) || '—') : '—')
    },
    {
      title: 'Дата',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (d: string) => dayjs(d).format('DD.MM.YYYY')
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (s: string) => <Tag color={statusColor[s] || 'default'}>{s}</Tag>
    },
    {
      title: 'Маршрут',
      dataIndex: 'routeId',
      key: 'routeId',
      width: 100,
      render: (id?: number) => id ? `#${id}` : '—'
    },
    {
      title: 'Заявка',
      dataIndex: 'applicationId',
      key: 'applicationId',
      width: 100,
      render: (id?: number) => id ? `#${id}` : '—'
    },
    {
      title: 'Итого',
      key: 'totals',
      render: (_, r) => (
        <Space size={8} wrap>
          <Text type="secondary">Мест: {r.totalPlaces ?? 0}</Text>
          <Text type="secondary">Вес: {Number(r.totalWeightKg || 0).toFixed(2)}</Text>
          <Text type="secondary">Объем: {Number(r.totalVolumeM3 || 0).toFixed(2)}</Text>
        </Space>
      )
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => navigate(`/tn/${record.id}`)}>Открыть</Button>
          <Button size="small" onClick={async (e) => {
            e.stopPropagation();
            try {
              const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
              const res = await fetch(`${API_BASE_URL}/tn/${record.id}/copy`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
              if (res.ok) {
                const data = await res.json();
                message.success('ТН скопирована');
                navigate(`/tn/${data.id}`);
              } else {
                const error = await res.json();
                message.error(error?.error || 'Ошибка копирования');
              }
            } catch (e: any) {
              message.error('Ошибка копирования');
            }
          }}>Копировать</Button>
          <Button size="small" danger onClick={async (e) => {
            e.stopPropagation();
            try {
              const ok = window.confirm('Удалить ТН? Действие необратимо.');
              if (!ok) return;
              await deleteTN(record.id as any).unwrap();
              message.success('ТН удалена');
              refetch();
            } catch (e: any) {
              message.error(e?.data?.error || 'Ошибка удаления');
            }
          }}>Удалить</Button>
        </Space>
      )
    }
  ];

  return (
    <PageLayout
      title="Транспортные накладные"
      subtitle="Список и оформление транспортных накладных"
      actions={<Button type="primary" onClick={() => navigate('/tn/new')}>Новая ТН</Button>}
    >
      <Card style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
        <DataTable
          columns={columns}
          dataSource={data?.items || []}
          rowKey="id"
          loading={isLoading}
          onRow={(record) => ({ onClick: () => navigate(`/tn/${record.id}`) })}
          pagination={{
            current: page,
            pageSize: limit,
            total: data?.total || 0,
            showSizeChanger: true,
            onChange: (p, l) => { setPage(p); setLimit(l); }
          }}
          size="middle"
        />
      </Card>
    </PageLayout>
  );
};

export default TNPage;



